import { WalletContextState } from '@solana/wallet-adapter-react';
import { TRPC } from '../../contexts/TRPC';
import { normalizeMintingError } from '../errorHandling';
import { captureAndReportError } from '../errorReporting';
import { mintAppNftWorkflow } from './mintNftWorkflow';

export interface AppNftMintingData {
  dappId: string;
  dappName: string;
  dappIconUrl?: string | null;
  appWebsite?: string | null;
  packageName: string | null;
  walletAddress: string;
}

export interface AppNftMintingResult {
  success: boolean;
  mintAddress?: string;
  transactionSignature?: string;
  error?: Error;
}

type UploadAppNftDataFunction = (
  wallet: WalletContextState,
  appData: {
    name: string;
    website?: string;
    androidPackage: string;
    address: string;
  },
  iconFile: File,
  trpc: TRPC['client'],
  onProgress?: (
    progress: number,
    message?: string,
    isSignatureInProgress?: boolean,
  ) => void,
) => Promise<{ metadataUri: string }>;

export async function mintAppNft(
  wallet: WalletContextState,
  dappData: AppNftMintingData,
  iconFile: File,
  trpc: TRPC['client'],
  uploadAppNftData: UploadAppNftDataFunction,
  onProgress?: (status: string) => void,
): Promise<AppNftMintingResult> {
  try {
    if (!iconFile) {
      throw new Error('App icon is required to mint the App NFT.');
    }

    let lastProgressStatus = '';
    const reportProgress = (status?: string) => {
      if (!status || status === lastProgressStatus) {
        return;
      }
      lastProgressStatus = status;
      onProgress?.(status);
    };

    const result = await mintAppNftWorkflow({
      wallet,
      trpc,
      uploadAppNftData,
      app: {
        dappId: dappData.dappId,
        name: dappData.dappName,
        website: dappData.appWebsite,
        androidPackage: dappData.packageName || '',
        publisherAddress: dappData.walletAddress,
      },
      iconFile,
      onStatus: reportProgress,
      onUploadProgress: (_progress, message, isSignatureInProgress) => {
        if (isSignatureInProgress) {
          reportProgress('Waiting for wallet signature...');
          return;
        }
        reportProgress(message);
      },
    });

    return {
      success: true,
      mintAddress: result.mintAddress,
      transactionSignature: result.transactionSignature,
    };
  } catch (error) {
    const normalizedError = normalizeMintingError(error);

    if (!normalizedError.isUserRejection) {
      captureAndReportError(error, {
        operation: 'App NFT mint error',
        component: 'mintAppNft',
      });
    }

    return {
      success: false,
      error: new Error(normalizedError.message),
    };
  }
}
